import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import { Tooltip, Box, MenuItem, Select, Autocomplete, TextField, Typography, Chip } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';

/** Material UI Icons. */
import BorderColorIcon from '@mui/icons-material/BorderColor';

/** Internal Components. */
import { customized_style } from 'shared/config/Styles';

/**
 * CustomAutocompleteCell is a component designed to render a multi-select Autocomplete input within a MUI DataGrid cell.
 * It allows the user to select multiple options from a provided list.
 */
const CustomAutocompleteCell = ({ params, options }) => {
    const [value, setValue] = useState([]);

    useEffect(() => {
        // Set the initial value when params.value is an array of IDs
        if (Array.isArray(params.value)) {
            setValue(options.filter((option) => params.value.includes(option.id)));
        }
    }, [params.value, options]);

    const handleChange = (event, newValue) => {
        setValue(newValue);
        const ids = newValue.map((item) => item.id);
        params.api.setEditCellValue({ id: params.id, field: params.field, value: ids });
    };

    return (
        <Autocomplete
            multiple
            options={options}
            value={value}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={handleChange}
            renderInput={(params) => <TextField {...params} multiline fullWidth sx={{ fontSize: 11 }} />}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                    const allProps = getTagProps({ index });
                    const { key, ...chipProps } = allProps;

                    return <Chip key={key} size="small" label={option.name} {...chipProps} />;
                })
            }
            sx={{
                height: '100%',
                width: '100%',
                '& .MuiInputBase-input': {
                    fontSize: '12px',
                },
                '& .MuiInputBase-root': {
                    p: 0,
                },
                '& .MuiButtonBase-root': {
                    my: 0,
                },
            }}
        />
    );
};

export const teamCol = (handleOpenDialog, tradePartner) => [
    {
        field: 'id',
        headerName: 'ID',
        headerAlign: 'center',
        headerClassName: 'normal-column-style',
        align: 'center',
        flex: 0.2,
    },
    {
        field: 'name',
        headerName: 'Team',
        headerClassName: 'normal-column-style',
        headerAlign: 'center',
        align: 'center',
        editable: true,
        flex: 0.3,
        valueGetter: (params) => params?.toUpperCase(),
    },
    {
        field: 'il_trade_partner_id',
        headerName: 'Client List',
        headerClassName: 'normal-column-style',
        headerAlign: 'center',
        align: 'center',
        flex: 2,
        renderCell: (params) => {
            const clientContent = params.row.il_trade_partner_id
                .map((id) => {
                    const client = tradePartner.find((item) => item.id === id);
                    return client ? client.name : '';
                })
                .filter((name) => name)
                .sort((a, b) => a.localeCompare(b))
                .join(', ');

            return (
                <Tooltip title={clientContent} placement="bottom" arrow>
                    <Box
                        sx={{
                            fontSize: 12,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                            width: '100%',
                        }}
                    >
                        {clientContent}
                    </Box>
                </Tooltip>
            );
        },
    },
    {
        field: 'actions',
        type: 'actions',
        headerName: '',
        headerClassName: 'normal-column-style',
        align: 'center',
        headerAlign: 'center',
        flex: 0.2,
        getActions: ({ id, row }) => {
            const isDisabled = row.status === false;
            return [
                <GridActionsCellItem
                    sx={{
                        bgcolor: '#003a52',
                        color: 'white',
                        '&:hover': { color: '#003a52' },
                        p: '5px',
                    }}
                    icon={<BorderColorIcon sx={{ fontSize: '13px' }} />}
                    label="dialog_open"
                    onClick={() => handleOpenDialog(id)}
                    color="secondary"
                    disabled={isDisabled}
                />,
            ];
        },
    },
    {
        field: 'status',
        headerName: 'Active',
        headerClassName: 'normal-separator-column-style',
        headerAlign: 'center',
        align: 'center',
        type: 'boolean',
        editable: true,
        flex: 0.2,
    },
];

export const userCol = (
    handleOpenDialog,
    handleOpenSubDialog,
    teamList,
    getContentById_team,
    getContentById_client
) => [
    {
        field: 'id',
        headerName: 'ID',
        headerAlign: 'center',
        headerClassName: 'normal-column-style',
        align: 'center',
        flex: 0.3,
    },
    {
        field: 'first_name',
        headerName: 'First Name',
        headerClassName: 'normal-column-style',
        headerAlign: 'center',
        align: 'center',
        flex: 0.8,
    },
    {
        field: 'last_name',
        headerName: 'Last Name',
        headerClassName: 'normal-column-style',
        headerAlign: 'center',
        align: 'center',
        flex: 0.8,
    },
    {
        field: 'job_title',
        headerName: 'Job Title',
        headerClassName: 'normal-separator-column-style',
        headerAlign: 'center',
        align: 'center',
        flex: 1,
    },
    {
        field: 'il_team_id',
        headerName: 'Team',
        headerClassName: 'normal-separator-column-style',
        headerAlign: 'center',
        align: 'center',
        editable: true,
        flex: 1,
        renderCell: (params) => {
            const name = params.value.map((id) => getContentById_team(id)).join(', ');
            return name;
        },
        renderEditCell: (params) => {
            return (
                <Select
                    multiple
                    value={params.value || []}
                    onChange={(event) => {
                        params.api.setEditCellValue({
                            id: params.id,
                            field: params.field,
                            value: event.target.value,
                        });
                    }}
                    displayEmpty
                    renderValue={(selected) => {
                        return selected.map((id) => getContentById_team(id)).join(', ') || 'Select';
                    }}
                    sx={{ width: '100%', fontSize: 13 }}
                >
                    {teamList.map((option) => (
                        <MenuItem key={option.id} value={option.id} sx={{ fontSize: 12 }}>
                            {option.name}
                        </MenuItem>
                    ))}
                </Select>
            );
        },
    },
    {
        field: 'il_trade_partner_id',
        headerName: 'Client List',
        headerClassName: 'normal-separator-column-style',
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => {
            const name = params.value
                .map((id) => getContentById_client(id))
                .sort((a, b) => a.localeCompare(b))
                .join(', ');
            return (
                <Tooltip title={name} arrow>
                    <Box
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                            width: '100%',
                            fontSize: 12,
                        }}
                    >
                        {name}
                    </Box>
                </Tooltip>
            );
        },
        flex: 4,
    },
    {
        field: 'actions',
        type: 'actions',
        headerName: '',
        headerClassName: 'normal-column-style',
        align: 'center',
        headerAlign: 'center',
        flex: 0.2,
        getActions: ({ id, row }) => {
            const isDisabled = row.status === false;
            return [
                <GridActionsCellItem
                    sx={{
                        bgcolor: '#003a52',
                        color: 'white',
                        '&:hover': { color: '#003a52' },
                        p: '5px',
                    }}
                    icon={<BorderColorIcon sx={{ fontSize: '13px' }} />}
                    label="dialog_open"
                    onClick={() => handleOpenDialog(id)}
                    color="secondary"
                    disabled={isDisabled}
                />,
            ];
        },
    },
    {
        field: 'subclient',
        headerName: 'Sub Client',
        headerClassName: 'normal-separator-column-style',
        headerAlign: 'center',
        align: 'center',
        flex: 3,
        renderCell: (params) => {
            if (params.value) {
                const name = params.value
                    .map((id) => getContentById_client(id))
                    .sort((a, b) => a.localeCompare(b))
                    .join(', ');
                return (
                    <Tooltip title={name} arrow>
                        <Box
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                                width: '100%',
                                fontSize: 12,
                            }}
                        >
                            {name}
                        </Box>
                    </Tooltip>
                );
            }
        },
    },
    {
        field: 'actions2',
        type: 'actions',
        headerName: '',
        headerClassName: 'normal-column-style',
        align: 'center',
        headerAlign: 'center',
        flex: 0.2,
        getActions: ({ id, row }) => {
            const isDisabled = row.status === false;
            return [
                <GridActionsCellItem
                    sx={{
                        bgcolor: '#003a52',
                        color: 'white',
                        '&:hover': { color: '#003a52' },
                        p: '5px',
                    }}
                    icon={<BorderColorIcon sx={{ fontSize: '13px' }} />}
                    label="dialog_open"
                    onClick={() => handleOpenSubDialog(id)}
                    color="secondary"
                    disabled={isDisabled}
                />,
            ];
        },
    },
    {
        field: 'status',
        headerName: 'Active',
        type: 'boolean',
        headerClassName: 'normal-separator-column-style',
        headerAlign: 'center',
        align: 'center',
        editable: true,
        flex: 0.5,
    },
];

export const clientCols = [
    { field: 'id', headerName: 'ID', align: 'center', headerAlign: 'center', hide: true },
    {
        field: 'name',
        headerName: 'Name',
        flex: 3,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
            <Box
                sx={{
                    fontSize: 12,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                {params.value}
            </Box>
        ),
    },
];

export const subClientCol = (tradePartner) => [
    {
        field: 'id',
        headerName: 'ID',
        align: 'center',
        headerAlign: 'center',
        hide: true,
    },
    {
        field: 'il_trade_partner_id_forwarder',
        headerName: 'Client',
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
            <Typography sx={customized_style.itemDatagrid_Typography}>{params.value.name}</Typography>
        ),
    },
    {
        field: 'il_trade_partner_id_shipper',
        headerName: 'Sub Client',
        flex: 3,
        align: 'center',
        headerAlign: 'center',
        editable: true,
        renderCell: (params) => {
            const selectedNames = tradePartner
                .filter((option) => params.value?.includes(option.id))
                .map((option) => option.name)
                .join(', ');
            return (
                <span
                    style={{
                        display: 'block',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        maxWidth: '500px',
                        paddingTop: '8px',
                        paddingBottom: '8px',
                    }}
                >
                    {selectedNames}
                </span>
            );
        },
        renderEditCell: (params) => {
            return (
                <CustomAutocompleteCell
                    params={params}
                    options={tradePartner.filter((item) => item.partner_type === 'Shipper/Consignee')}
                />
            );
        },
    },
];
